import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Row, Col, Card, Badge, Button, Spinner, Table, Image } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";

export default function ProductDetail() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const location = useLocation();

    const initialProduct = location.state?.product || null;
    const [product, setProduct] = useState(initialProduct);
    const [loading, setLoading] = useState(!initialProduct);

    const imgBaseUrl = process.env.REACT_APP_IMG_URL || "http://localhost:8000/storage/";

    useEffect(() => {
        if (!initialProduct) {
            const fetchProductDetail = async () => {
                try {
                    const res = await axios.get(
                        `${process.env.REACT_APP_API_URL}/admin/products/detail`,
                        {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                            },
                        }
                    );
                    setProduct(res.data?.data || res.data);
                } catch (err) {
                    console.error("Failed to fetch product details:", err);
                } finally {
                    setLoading(false);
                }
            };
            fetchProductDetail();
        }
    }, [initialProduct]);

    if (loading || !product) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("Loading product detail...")}</p>
                </div>
            </PageLayout>
        );
    }

    // Parse buyer details string safely
    let buyerDetails = null;
    if (product.order?.order?.buyer_details) {
        try {
            buyerDetails = typeof product.order.order.buyer_details === "string"
                ? JSON.parse(product.order.order.buyer_details)
                : product.order.order.buyer_details;
        } catch (e) {
            buyerDetails = null;
        }
    }

    const orderData = product.order?.order;
    const orderDetail = product.order?.order_detail;

    const decodeHtmlEntities = (htmlStr) => {
        if (!htmlStr) return "";
        const txt = document.createElement("textarea");
        txt.innerHTML = htmlStr;
        return txt.value;
    };

    return (
        <PageLayout>
            <Row>
                {/* Header / Breadcrumb */}
                <Col xl={12}>
                    <div className="mc-card mb-4 border-0 shadow-sm rounded-3 p-3 bg-white">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="mc-breadcrumb-title mb-1 fw-bold text-dark">
                                    {product.title} <span className="text-muted fs-6 font-monospace">({product.product_code})</span>
                                </h4>
                                <ul className="mc-breadcrumb-list list-inline mb-0 text-muted small">
                                    <li className="list-inline-item">
                                        <Link to="#" className="text-decoration-none text-muted">{t("Home")}</Link> /
                                    </li>
                                    <li className="list-inline-item">
                                        <Link to="/products" className="text-decoration-none text-muted">{t("Products")}</Link> /
                                    </li>
                                    <li className="list-inline-item active text-primary fw-semibold">{t("Detail")}</li>
                                </ul>
                            </div>
                            <Button variant="outline-primary" size="sm" className="px-3 rounded-pill" onClick={() => navigate(-1)}>
                                &larr; {t("Back")}
                            </Button>
                        </div>
                    </div>
                </Col>

                {/* Left Column: Product Info & Order Details */}
                <Col xl={8}>
                    {/* Main Product Overview */}
                    <Card className="mb-4 border-0 shadow-sm rounded-3">
                        <Card.Header className="bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 fw-bold text-uppercase text-secondary">Product Overview</h6>
                            <div className="d-flex gap-2">
                                <Badge bg="light" className="text-dark border">Condition: {product.product_condition}</Badge>
                                <Badge bg="light" className="text-dark border">Style: {product.selling_style}</Badge>
                                <Badge bg={product.sold_on ? "success" : "warning"} className="text-white">
                                    {product.sold_on ? "Sold" : product.status}
                                </Badge>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {/* Product Images & Description */}
                            <Row className="g-4 mb-4">
                                <Col md={5} className="text-center border-end">
                                    <div className="mb-3">
                                        <span className="d-block text-muted small mb-1 fw-semibold">Primary Image</span>
                                        {product.img?.first_img && (
                                            <Image
                                                src={`${imgBaseUrl}${product.img.first_img}`}
                                                alt="First"
                                                fluid
                                                rounded
                                                className="border p-1 shadow-sm"
                                                style={{ maxHeight: "200px", objectFit: "cover" }}
                                            />
                                        )}
                                    </div>
                                    {product.img?.card_img && (
                                        <div>
                                            <span className="d-block text-muted small mb-1 fw-semibold">Card Image</span>
                                            <Image
                                                src={`${imgBaseUrl}${product.img.card_img}`}
                                                alt="Card"
                                                fluid
                                                rounded
                                                className="border p-1 shadow-sm"
                                                style={{ maxHeight: "100px", objectFit: "cover" }}
                                            />
                                        </div>
                                    )}
                                </Col>

                                <Col md={7}>
                                    <h6 className="fw-bold text-dark">Short Description</h6>
                                    <p className="text-muted small mb-3">{product.short_description}</p>

                                    <h6 className="fw-bold text-dark">Full Description</h6>
                                    <div
                                        className="p-3 border rounded-3 bg-light text-muted small mb-3"
                                        dangerouslySetInnerHTML={{
                                            __html: decodeHtmlEntities(product.description)
                                        }}
                                    />

                                    <Row className="g-2 pt-2 border-top">
                                        <Col xs={6} sm={3}>
                                            <span className="text-muted d-block small">Quantity</span>
                                            <strong className="text-dark">{product.qty}</strong>
                                        </Col>
                                        <Col xs={6} sm={3}>
                                            <span className="text-muted d-block small">Brand</span>
                                            <strong className="text-dark">{product.brand?.name}</strong>
                                        </Col>
                                        <Col xs={6} sm={3}>
                                            <span className="text-muted d-block small">Category</span>
                                            <strong className="text-dark">{product.category?.name}</strong>
                                        </Col>
                                        <Col xs={6} sm={3}>
                                            <span className="text-muted d-block small">Store</span>
                                            <strong className="text-dark">{product.store?.name}</strong>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>

                            {/* Gallery Images */}
                            {Array.isArray(product.images) && product.images.length > 0 && (
                                <div className="mb-4">
                                    <h6 className="fw-bold text-dark mb-2">Gallery Images</h6>
                                    <div className="d-flex gap-2 flex-wrap">
                                        {product.images.map((imgObj, idx) => (
                                            <Image
                                                key={idx}
                                                src={`${imgBaseUrl}${imgObj.image}`}
                                                alt="Gallery"
                                                rounded
                                                className="border p-1 shadow-sm"
                                                style={{ width: "80px", height: "80px", objectFit: "cover" }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Pricing Cards */}
                            <h6 className="fw-bold text-dark mb-2">Pricing Details</h6>
                            <Row className="g-3 mb-4">
                                <Col sm={3}>
                                    <div className="p-3 border rounded-3 bg-light text-center">
                                        <span className="text-muted d-block small">Buying Price</span>
                                        <h6 className="mb-0 text-dark fw-bold">${product.pricing?.buying_price}</h6>
                                    </div>
                                </Col>
                                <Col sm={3}>
                                    <div className="p-3 border rounded-3 bg-light text-center">
                                        <span className="text-muted d-block small">Sell Price ($)</span>
                                        <h6 className="mb-0 text-dark fw-bold">${product.pricing?.sell_price_dollar}</h6>
                                    </div>
                                </Col>
                                <Col sm={3}>
                                    <div className="p-3 border rounded-3 bg-light text-center">
                                        <span className="text-muted d-block small">Shipping Charges</span>
                                        <h6 className="mb-0 text-dark fw-bold">${product.pricing?.shipping_charges_dollar}</h6>
                                    </div>
                                </Col>
                            </Row>

                            {/* Internal Assignments */}
                            <h6 className="fw-bold text-dark mb-2">Internal System Users</h6>
                            <Row className="g-2 p-3 bg-light rounded-3 text-muted small">
                                <Col sm={4}>Issuer: <strong className="text-dark">{product.issuer?.name}</strong></Col>
                                <Col sm={4}>Lister: <strong className="text-dark">{product.lister?.name}</strong></Col>
                                <Col sm={4}>Market Placer: <strong className="text-dark">{product.market_placer?.name}</strong></Col>
                            </Row>
                        </Card.Body>
                        <Card.Footer className="bg-white border-top text-muted small d-flex justify-content-between py-2">
                            <span>Created At: {new Date(product.created_at).toLocaleString()}</span>
                            <span>Updated At: {new Date(product.updated_at).toLocaleString()}</span>
                        </Card.Footer>
                    </Card>

                    {/* Full Order & Buyer Information */}
                    {orderData && (
                        <Card className="mb-4 border-0 shadow-sm rounded-3">
                            <Card.Header className="bg-white py-3 border-bottom">
                                <h6 className="mb-0 fw-bold text-uppercase text-secondary">Order & Buyer Details</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-4 g-3">
                                    <Col md={6}>
                                        <h6 className="fw-bold text-dark mb-2">Order Master Info</h6>
                                        <Table size="sm" borderless responsive className="small align-middle bg-light rounded-3 p-2">
                                            <tbody>
                                                <tr>
                                                    <td className="text-muted">Order Number</td>
                                                    <td className="fw-semibold text-dark">{orderData.order_number}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">Payment Status</td>
                                                    <td><Badge bg="warning" className="text-dark">{orderData.payment_status}</Badge></td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">Delivery Status</td>
                                                    <td><Badge bg="info">{orderData.delivery_status}</Badge></td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">Tracking Number</td>
                                                    <td className="fw-semibold text-dark">{orderData.tracking_number}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">Courier Company</td>
                                                    <td className="fw-semibold text-dark">{orderData.courier_company?.name}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">Delivery Days</td>
                                                    <td className="fw-semibold text-dark">{orderData.delivery_days} Days</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">Payment Date</td>
                                                    <td className="fw-semibold text-dark">{orderData.payment_date ? new Date(orderData.payment_date).toLocaleString() : "N/A"}</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Col>

                                    <Col md={6}>
                                        <h6 className="fw-bold text-dark mb-2">Financial Breakdown</h6>
                                        <Table size="sm" borderless responsive className="small align-middle bg-light rounded-3 p-2">
                                            <tbody>
                                                <tr>
                                                    <td className="text-muted">Subtotal</td>
                                                    <td className="fw-semibold text-dark">${orderData.subtotal}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">Shipping Charges</td>
                                                    <td className="fw-semibold text-dark">${orderData.shipping_charges}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">Grand Total</td>
                                                    <td className="fw-bold text-success">${orderData.grand_total}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">Ordered At</td>
                                                    <td className="fw-semibold text-dark">{new Date(orderData.ordered_at).toLocaleString()}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">Processing At</td>
                                                    <td className="fw-semibold text-dark">{new Date(orderData.processing_at).toLocaleString()}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">Posted At</td>
                                                    <td className="fw-semibold text-dark">{new Date(orderData.posted_at).toLocaleString()}</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Col>
                                </Row>

                                {/* Buyer Details */}
                                <h6 className="fw-bold text-dark mb-2">Buyer Profile & Shipping Address</h6>
                                <Row className="g-3 mb-3">
                                    <Col md={6}>
                                        <div className="p-3 border rounded-3 bg-light small h-100">
                                            <span className="text-primary fw-bold d-block mb-1">Ordered By User</span>
                                            <div>Name: <strong className="text-dark">{orderData.ordered_by?.name}</strong></div>
                                            <div>Email: <strong className="text-dark">{orderData.ordered_by?.email}</strong></div>
                                            <div>Role: <strong className="text-dark">{orderData.ordered_by?.role}</strong></div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="p-3 border rounded-3 bg-light small h-100">
                                            <span className="text-primary fw-bold d-block mb-1">Buyer System Info</span>
                                            <div>Buyer Name: <strong className="text-dark">{orderData.buyer?.buyer_name}</strong></div>
                                            <div>Phone: <strong className="text-dark">{orderData.buyer?.buyer_phone}</strong></div>
                                            <div>Address: <strong className="text-dark">{orderData.buyer?.buyer_address}</strong></div>
                                            <div>VAT ID: <strong className="text-dark">{orderData.buyer?.vat_id || "N/A"}</strong></div>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Parsed JSON Buyer Details */}
                                {buyerDetails && (
                                    <div className="p-3 border rounded-3 bg-light mb-3 small">
                                        <span className="text-primary fw-bold d-block mb-1">Parsed Buyer Details</span>
                                        <Row className="g-2">
                                            <Col sm={4}>Name: <strong className="text-dark">{buyerDetails.name}</strong></Col>
                                            <Col sm={4}>Email: <strong className="text-dark">{buyerDetails.email}</strong></Col>
                                            <Col sm={4}>Phone: <strong className="text-dark">{buyerDetails.phone}</strong></Col>
                                            <Col sm={4}>City: <strong className="text-dark">{buyerDetails.city}</strong></Col>
                                            <Col sm={4}>Country: <strong className="text-dark">{buyerDetails.country}</strong></Col>
                                            <Col sm={4}>VAT ID: <strong className="text-dark">{buyerDetails.vat_id}</strong></Col>
                                            <Col sm={12}>Address: <strong className="text-dark">{buyerDetails.address}</strong></Col>
                                        </Row>
                                    </div>
                                )}

                                {/* Order Item / Detail */}
                                {orderDetail && (
                                    <>
                                        <h6 className="fw-bold text-dark mt-3 mb-2">Item Purchased Detail</h6>
                                        <Table size="sm" bordered responsive className="small align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Description</th>
                                                    <th>Quantity</th>
                                                    <th>Unit Price</th>
                                                    <th>Shipping</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>{orderDetail.description}</td>
                                                    <td>{orderDetail.quantity}</td>
                                                    <td>${orderDetail.unit_price}</td>
                                                    <td>${orderDetail.shipping}</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </>
                                )}

                                {/* Marketplace details */}
                                {orderData.marketplace && (
                                    <div className="d-flex align-items-center gap-2 mt-3 p-2 border rounded-3 bg-light">
                                        {orderData.marketplace.logo && (
                                            <Image
                                                src={`${imgBaseUrl}${orderData.marketplace.logo}`}
                                                alt="Marketplace Logo"
                                                style={{ width: "24px", height: "24px", objectFit: "contain" }}
                                            />
                                        )}
                                        <span className="small text-muted">
                                            Marketplace: <strong className="text-dark">{orderData.marketplace.name}</strong> ({orderData.marketplace.type}) -{" "}
                                            <a href={orderData.marketplace.base_url} target="_blank" rel="noreferrer" className="text-decoration-none">
                                                {orderData.marketplace.base_url}
                                            </a>
                                        </span>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}
                </Col>

                {/* Right Column: Timeline */}
                <Col xl={4}>
                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Header className="bg-white py-3 border-bottom">
                            <h6 className="mb-0 fw-bold text-uppercase text-secondary">Product Movement Timeline</h6>
                        </Card.Header>
                        <Card.Body className="p-4">
                            {Array.isArray(product.timeline) && product.timeline.length > 0 ? (
                                <div className="timeline-container position-relative">
                                    {/* Continuous Vertical Line */}
                                    <div
                                        className="position-absolute bg-primary bg-opacity-25"
                                        style={{
                                            left: "11px",
                                            top: "10px",
                                            bottom: "10px",
                                            width: "3px",
                                            borderRadius: "2px",
                                            zIndex: 1,
                                        }}
                                    />

                                    {product.timeline.map((item, index) => (
                                        <div key={index} className="position-relative ps-4 pb-4" style={{ zIndex: 2 }}>
                                            {/* Timeline Bullet Indicator Point */}
                                            <div
                                                className="position-absolute bg-primary rounded-circle border border-2 border-white shadow-sm"
                                                style={{
                                                    left: "5px",
                                                    top: "4px",
                                                    width: "15px",
                                                    height: "15px",
                                                }}
                                            />

                                            <div className="bg-light p-3 rounded-3 border">
                                                <div className="fw-bold text-dark small">{item.title}</div>
                                                <small className="text-muted d-block mb-1">{item.time}</small>

                                                {/* <div className="d-flex gap-1 flex-wrap mb-1">
                                                    {item.type && <Badge bg="dark" className="small">{item.type}</Badge>}
                                                    {item.status && <Badge bg="success" className="small">{item.status}</Badge>}
                                                </div> */}

                                                {item.movement_type && (
                                                    <div className="small text-primary fw-semibold mt-1">
                                                        {item.movement_type}
                                                    </div>
                                                )}

                                                {item.handover_image && (
                                                    <div className="mt-2 border-top pt-2">
                                                        <span className="d-block text-muted small mb-1">Handover Proof:</span>
                                                        <a
                                                            href={`${imgBaseUrl}${item.handover_image}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <Image
                                                                src={`${imgBaseUrl}${item.handover_image}`}
                                                                alt="Handover Image"
                                                                rounded
                                                                className="border shadow-sm"
                                                                style={{
                                                                    width: "60px",
                                                                    height: "60px",
                                                                    objectFit: "cover",
                                                                }}
                                                            />
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted mb-0 small">No timeline history recorded.</p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}